<template>
  <div class="fcm">
    <f7-link class="fcm-bar" @click="expanded = true" role="button" tabindex="0" @keydown.enter="expanded = true" @keydown.space.prevent="expanded = true">
      <div class="fcm-avatar" :style="avatarStyle" v-if="userAccount.avatar">
        <img class="fcm-avatar" :src="userAccount.avatar" :alt="$t('common.a11y.userAvatar')" />
      </div>
      <div class="fcm-avatar" :style="avatarStyle" v-else>
        {{ userInitials }}
      </div>
      <div class="fcm-input">{{ $t('feed.shareWithCommunity') }}</div>
      <f7-icon material="photo" :size="18" color="#1A6B42" />
    </f7-link>
    <AdaptiveSheet
      v-model:opened="expanded"
      class="fcm-popup"
      swipe-to-close
      backdrop
      close-by-backdrop-click
      close-on-escape
      close-by-outside-click
      :tablet-fullscreen="true"
      full-screen
    >
      <f7-view>
        <f7-page name="feed-composer">
              <f7-navbar>
                <f7-nav-title>{{ $t('feed.newPost') }}</f7-nav-title>
                <f7-nav-right>
                  <f7-link sheet-close popup-close
                    ><f7-icon f7="multiply" :size="18"
                  /></f7-link>
                </f7-nav-right>
              </f7-navbar>
              <AppLayout>
                <template #main>
              <div class="fcm-compose">
                <div class="fcm-compose-header">
                  <div
                    class="fcm-avatar"
                    :style="avatarStyle"
                    v-if="userAccount.avatar"
                  >
                    <img
                      class="fcm-avatar fcm-avatar--lg"
                      :src="userAccount.avatar"
                      :alt="$t('common.a11y.userAvatar')"
                    />
                  </div>
                  <div
                    class="fcm-avatar fcm-avatar--lg"
                    v-else
                    :style="avatarStyle"
                  >
                    {{ userInitials }}
                  </div>
                  <div>
                    <div class="fcm-compose-name">{{ userName }}</div>
                    <select v-model="visibility" class="fcm-visibility-select">
                      <option value="Public">{{ $t('feed.public') }}</option>
                      <option value="Group">{{ $t('feed.group') }}</option>
                    </select>
                  </div>
                </div>

                <f7-segmented round class="fcm-type-toggle" v-if="allowPolls" tag="div" :strong="false">
                  <f7-button
                    round
                    raised
                    large
                    class="post-tab"
                    :fill="mode === 'post'"
                    :class="{ active: mode === 'post' }"
                    @click="mode = 'post'"
                  >
                    {{ $t('feed.postType') }}
                  </f7-button>
                  <f7-button
                    round
                    large
                    raised
                    class="poll-tab"
                    :fill="mode === 'poll'"
                    :class="{ active: mode === 'poll' }"
                    @click="mode = 'poll'"
                  >
                    {{ $t('feed.pollLabel') }}
                  </f7-button>
                </f7-segmented>

                <PostEditor
                  v-if="mode === 'post'"
                  :user-name="userName"
                  :user-avatar="userAvatar"
                  :creating="creatingPost"
                  :submit-label="$t('feed.publish')"
                  @submit="onPostSubmit"
                />

                <PollCreate
                  v-else
                  :opened="expanded"
                  :currentEntity="null"
                  @poll-created="onPollCreated"
                />
              </div>
            </template>
           </AppLayout>
        </f7-page>
      </f7-view>
    </AdaptiveSheet>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import i18n from '../../js/i18n.js'
import { f7 } from "framework7-vue";
import PollCreate from "../pollCreate.vue";
import PostEditor from "./PostEditor.vue";
import AppLayout from "../common/AppLayout.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import { closeAdaptiveModal } from "../../js/composables/useAdaptiveModal.js";
import { useAccountStore } from "../../js/pinia-stores/account.js";
import { useAuthStore } from "../../js/pinia-stores/auth.js";

const { t } = i18n.global
const props = defineProps({
  userName: { type: String, default: "" },
  userAvatar: { type: String, default: "" },
  allowPolls: { type: Boolean, default: true },
});

const emit = defineEmits(["create-post", "create-poll"]);

const accountStore = useAccountStore();
const authStore = useAuthStore();
const expanded = ref(false);
const mode = ref("post");
const creatingPost = ref(false);
const visibility = ref("Public");

const userInitials = computed(() => {
  return props.userName
    .split(" ")
    .map((w) => w[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
});
const userAccount = computed(() => {
  return accountStore.userAccount;
});
const avatarStyle = computed(() => {
  return { background: "#1A6B42", color: "#fff" };
});

async function onPostSubmit({ headline, text }) {
  creatingPost.value = true;
  try {
    let media = [];
    if (authStore.documentsUrl && authStore.documentsUrl.length > 0) {
      const shortId = f7.utils.id();
      await authStore.postR2Document({
        shortId,
        bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
      });
      media = authStore.documentsUrl.map((d) => d.url).filter(Boolean);
    }
    emit("create-post", {
      headline,
      text,
      visibility: visibility.value,
      media,
    });
    authStore.clearDocumentsUrl();
    mode.value = "post";
    expanded.value = false;
    f7.toast.show({
      text: t('feed.postCreated'),
      position: "center",
      closeTimeout: 1500,
    });
  } finally {
    creatingPost.value = false;
  }
}

function onPollCreated(poll) {
  emit("create-poll", poll);
  mode.value = "post";
  expanded.value = false;
  closeAdaptiveModal(".fcm-popup");
}
</script>

<style scoped>
.fcm-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--f7-page-bg-color, #fff);
  border-radius: 25px;
  padding: 8px 14px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  margin-bottom: 12px;
}
.fcm-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.fcm-avatar--lg {
  width: 40px;
  height: 40px;
  font-size: 14px;
}
.fcm-input {
  flex: 1;
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.fcm-compose {
  padding: 16px;
}
.fcm-compose-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.fcm-compose-name {
  font-size: 13px;
  font-weight: 500;
}
.fcm-visibility-select {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  border-radius: 8px;
  padding: 2px 6px;
  background: none;
}
.fcm-type-toggle {
  --f7-segmented-button-border-radius: 25px;
  --f7-segmented-button-font-size: 12px;
  --f7-segmented-button-font-weight: 500;
  --f7-segmented-button-text-color: var(--f7-on-surface-variant, #8e8e93);
  --f7-segmented-button-active-bg-color: var(--avocash-green, #1a6b42);
  --f7-segmented-button-active-text-color: #fff;
  margin-bottom: 12px;
}
.fcm-type-toggle .f7-button {
  flex: 1;
}
</style>
